import { CARS, currentStakes } from '../games/gt-racing/catalog.js';
import { createOffer, createRound, settlementFor } from '../games/gt-racing/race-core.js';

export const GAME_ID = 'gt-racing';
export const POSITIONS = CARS.length;

/** The tier the picked car's garage card shows: the terms the server draws this round under. */
function chanceBasisPointsFor(seed, carId) {
  return Math.round(createOffer(seed).profiles[carId].chance * 10_000);
}

/** The stakes the served configuration offers; the shipped three until it answers. */
export function stakes() { return currentStakes(); }
const STATE_KEY = 'zalafun.gt-racing.presentation.v1';
const STATE_VERSION = 2;
const CELEBRATION_SECONDS = 9;
const STAGES = new Set(['selecting', 'grid', 'countdown', 'racing', 'celebrating', 'result']);
const SETTLEMENT_STATUSES = new Set(['pending', 'confirmed', 'unconfirmed']);
const UNSETTLEABLE = new Set(['ROUND_NOT_FOUND', 'INVALID_ROUND_ID']);
const carExists = id => id === null || CARS.some(car => car.id === id);
const error = code => Object.assign(new Error(code), { code });

// A finish position is worth five points down to one, and only the winner is paid.
export const scoreFor = position => ({ value: POSITIONS - position + 1, position });

export function browserStorage() {
  try { return globalThis.localStorage; } catch { return { getItem: () => null, setItem() {} }; }
}

function deepFreeze(value) {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const item of Object.values(value)) deepFreeze(item);
  }
  return value;
}

function roundFrom(record) {
  if (!record || typeof record !== 'object') return null;
  // A started round keeps the stake and the chances it was priced under, even after a retune.
  if (!Number.isInteger(record.amount) || record.amount <= 0 || !record.selectedCarId || !carExists(record.selectedCarId)) return null;
  try { return deepFreeze(createRound({ ...record })); } catch { return null; }
}

function settlementFrom(record, position) {
  if (!record || !SETTLEMENT_STATUSES.has(record.status)) return null;
  if (!Number.isInteger(record.payout) || record.payout < 0) return null;
  if (!Number.isInteger(record.position) || record.position !== position) return null;
  return { status: record.status, payout: record.payout, position: record.position };
}

// Presentation storage only: the shared wallet owns every coin, so this store
// persists the selected race and its progress instead of any balance.
export function createRoundStore({ wallet, bridge, profile, analytics, storage = browserStorage() }) {
  // The garage opens on the first car so a round can start without a pick, and only
  // the stake is remembered from the round before; the car always returns to the top
  // of the grid.
  const defaultCarId = () => CARS[0]?.id ?? null;
  const empty = () => ({ stage: 'selecting', stake: stakes()[0], selectedCarId: defaultCarId(), round: null,
    elapsed: 0, walletRoundId: null, settlement: null, recentScenarios: [] });
  const walletSnapshot = () => wallet?.getSnapshot?.() ?? { balance: 0, activeRoundId: null };
  const identity = () => wallet?.getStorageIdentity?.() ?? 'guest';
  let account = identity();
  const key = () => account === 'guest' ? STATE_KEY : `${STATE_KEY}.${account}`;
  let state = empty(), submitting = false;
  const totalDuration = (round = state.round) => (round?.duration ?? 30) + CELEBRATION_SECONDS;

  function read() {
    const selected = { stake: state.stake, selectedCarId: state.selectedCarId };
    state = empty();
    state.stake = selected.stake; state.selectedCarId = selected.selectedCarId;
    try {
      const saved = JSON.parse(storage.getItem(key()));
      if (saved?.version !== STATE_VERSION || saved.account !== account) return;
      if (!STAGES.has(saved.stage) || !stakes().includes(saved.stake) || !carExists(saved.selectedCarId)) return;
      const round = roundFrom(saved.round);
      if ((saved.stage !== 'selecting') !== Boolean(round)) return;
      if (!Number.isFinite(saved.elapsed) || saved.elapsed < 0) return;
      if (saved.walletRoundId !== null && typeof saved.walletRoundId !== 'string') return;
      if (round && (round.amount !== saved.stake || round.selectedCarId !== saved.selectedCarId)) return;
      if (saved.stage !== 'result' && saved.settlement) return;
      const settlement = round ? settlementFrom(saved.settlement, settlementFor(round).selectedRank) : null;
      if (saved.stage === 'result' && (!settlement || saved.elapsed !== round.duration + CELEBRATION_SECONDS)) return;
      state.stake = saved.stake; state.selectedCarId = saved.selectedCarId; state.round = round;
      state.elapsed = Math.min(saved.elapsed, totalDuration(round)); state.stage = saved.stage;
      state.walletRoundId = saved.walletRoundId; state.settlement = settlement;
      state.recentScenarios = Array.isArray(saved.recentScenarios)
        ? saved.recentScenarios.filter(index => Number.isInteger(index) && index >= 0).slice(-10) : [];
    } catch { /* An unreadable presentation never starts or settles a wallet round. */ }
  }
  read();

  const encode = next => JSON.stringify({ version: STATE_VERSION, account, stage: next.stage, stake: next.stake,
    selectedCarId: next.selectedCarId, elapsed: next.elapsed, walletRoundId: next.walletRoundId,
    settlement: next.settlement, recentScenarios: next.recentScenarios,
    round: next.round ? { seed: next.round.seed, selectedCarId: next.round.selectedCarId, amount: next.round.amount,
      scenarioIndex: next.round.scenarioIndex, raceVersion: next.round.raceVersion, gridSeed: next.round.gridSeed,
      won: next.round.won } : null });

  // Only a durable write adopts the next presentation, so a failed save cannot
  // leave the page ahead of the wallet round it belongs to.
  function persist(next, required = false) {
    let saved = false;
    try {
      const encoded = encode(next);
      storage.setItem(key(), encoded);
      saved = storage.getItem(key()) === encoded;
    } catch { saved = false; }
    if (saved) state = next;
    else if (required) throw error('PRESENTATION_STORAGE_ERROR');
    return saved;
  }

  function advanceStage() {
    state.stage = state.elapsed < 2 ? 'grid' : state.elapsed < 5 ? 'countdown'
      : state.elapsed < state.round.duration + 5 ? 'racing' : 'celebrating';
  }

  const snapshot = () => {
    const coins = walletSnapshot();
    return Object.freeze({ stage: state.stage, stake: state.stake, selectedCarId: state.selectedCarId,
      round: state.round, elapsed: state.elapsed, totalDuration: totalDuration(), balance: coins.balance,
      recentScenarios: [...state.recentScenarios], submitting, settlement: state.settlement,
      walletBusy: coins.activeRoundId !== null });
  };

  async function requestSettlement(roundId, payout, score) {
    const active = bridge?.getSnapshot?.() ?? {};
    if (active.phase === 'active' && active.roundId === roundId) await bridge.settle(payout, score);
    else await wallet.settleRound(roundId, payout, score);
  }

  async function reconcile(settlement) {
    try {
      if (typeof state.walletRoundId !== 'string') throw error('ROUND_NOT_FOUND');
      await requestSettlement(state.walletRoundId, settlement.payout, scoreFor(settlement.position));
      persist({ ...state, settlement: { ...settlement, status: 'confirmed' } });
    } catch (failure) {
      persist({ ...state, settlement: { ...settlement, status: UNSETTLEABLE.has(failure?.code) ? 'unconfirmed' : 'pending' } });
    }
    return snapshot();
  }

  async function retrySettlement() {
    if (state.settlement?.status !== 'pending' || !state.round) return snapshot();
    return reconcile(state.settlement);
  }

  return {
    snapshot,
    // The frame loop reads progress without cloning the immutable round.
    progress: () => ({ stage: state.stage, elapsed: state.elapsed, round: state.round, totalDuration: totalDuration(),
      settled: state.settlement !== null }),

    selectStake(stake) {
      if (state.stage !== 'selecting' || !stakes().includes(stake)) throw error('INVALID_STAKE');
      state.stake = stake;
      return snapshot();
    },

    selectCar(carId) {
      if (state.stage !== 'selecting' || !carId || !CARS.some(car => car.id === carId)) throw error('INVALID_CAR');
      state.selectedCarId = carId;
      return snapshot();
    },

    async start({ seed, scenarioIndex }) {
      if (state.stage !== 'selecting' || state.round || submitting) throw error('ROUND_ACTIVE');
      if (!state.selectedCarId) throw error('INVALID_CAR');
      // The odds the garage showed are the terms of the bet, so the picked car's tier travels
      // with the round: the server draws the outcome at exactly that chance and pays its credit.
      const chanceBasisPoints = chanceBasisPointsFor(seed, state.selectedCarId);
      submitting = true;
      let started;
      try {
        const receipt = await bridge.begin(GAME_ID, state.stake, { chanceBasisPoints });
        // A receipt that carries the drawn outcome is the round the wallet will pay for; a
        // guest round (or an older service) carries none, and the race is drawn here instead.
        const drawn = receipt?.resolution?.won;
        const round = deepFreeze(createRound({ seed, selectedCarId: state.selectedCarId, amount: state.stake, scenarioIndex,
          won: typeof drawn === 'boolean' ? drawn : null }));
        started = { ...state, stage: 'grid', round, elapsed: 0, walletRoundId: receipt.roundId, settlement: null,
          recentScenarios: [...state.recentScenarios, round.scenarioIndex].slice(-10) };
        persist(started, true);
      } finally { submitting = false; }
      profile?.recordPlay?.(GAME_ID);
      analytics?.started?.(GAME_ID, state.stake);
      return snapshot();
    },

    advance(seconds) {
      if (!state.round || state.settlement || !Number.isFinite(seconds) || seconds < 0) return;
      state.elapsed = Math.min(totalDuration(), state.elapsed + seconds);
      advanceStage();
    },

    save() { return state.round ? persist(state) : true; },

    async finish() {
      if (!state.round || state.elapsed < totalDuration()) throw error('ROUND_NOT_FINISHED');
      if (state.settlement) return snapshot();
      const result = settlementFor(state.round);
      persist({ ...state, stage: 'result', elapsed: totalDuration(),
        settlement: { status: 'pending', payout: result.reward, position: result.selectedRank } }, true);
      const settled = await reconcile(state.settlement);
      if (state.settlement.status === 'confirmed') {
        profile?.recordScore?.(GAME_ID, scoreFor(result.selectedRank).value);
        analytics?.completed?.(GAME_ID, result.reward);
      }
      return settled;
    },

    retrySettlement,

    recover() {
      if (identity() !== account) { account = identity(); read(); }
      if (state.settlement?.status === 'pending') return retrySettlement();
      return Promise.resolve(snapshot());
    },

    refreshIdentity() {
      if (identity() === account) return false;
      account = identity();
      read();
      return true;
    },

    replay() {
      if (state.stage !== 'result') throw error('RESULT_NOT_READY');
      // The played stake carries into the next round; the car goes back to the grid
      // default. The presentation layer drops the stake later if the balance cannot
      // cover it.
      persist({ ...state, stage: 'selecting', selectedCarId: defaultCarId(), round: null,
        elapsed: 0, walletRoundId: null, settlement: null });
      return snapshot();
    },
  };
}
